import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ownerApi } from './ownerClient'
import type { CreateStaffPayload, StaffSession, StaffUser } from '../types'

/** Update the signed-in staff member's own display name; returns the refreshed session to adopt. */
export async function updateStaffProfileName(name: string): Promise<StaffSession> {
  const { data } = await ownerApi.put<StaffSession>('/auth/profile', { name })
  return data
}

/**
 * OWNER-only staff management (§16 Phase 6c) over `/api/users` (gated to ROLE_OWNER on the
 * backend). The tenant is ambient (staff JWT + `@TenantId`); these never send a tenant id.
 */

async function fetchStaff(): Promise<StaffUser[]> {
  const { data } = await ownerApi.get<StaffUser[]>('/users')
  return data
}

export function useStaffList(enabled: boolean) {
  return useQuery({ queryKey: ['staff'], queryFn: fetchStaff, enabled })
}

export function useCreateStaff() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CreateStaffPayload) => {
      const { data } = await ownerApi.post<StaffUser>('/users', payload)
      return data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['staff'] }),
  })
}

export function useDeactivateStaff() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await ownerApi.delete(`/users/${id}`)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['staff'] }),
  })
}
